/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState } from "react";
import { Text, Spacer, FlexContainer } from "@nordcloud/gnui";
import {
  useUpdateApplicationMutation,
  UpdateApplicationV2,
} from "~/generated/graphql";
import { InlineContactsUpdate, InlineUpdate } from "~/components/Inputs";
import { InlineContactPicker } from "~/components/Inputs/InlineContactPicker/InlineContactPicker";
import { useMutationHandler } from "~/hooks";
import { DetailsTitle } from "~/views/environments/components/DetailsTitle";

type Props = {
  id: string;
  name: string;
  owner: {
    id: string;
    name: string;
  };
  contacts: {
    id: string;
    name: string;
  }[];
  description: string;
  readonlyContacts?: boolean;
  readonlyOwner?: boolean;
};

type UpdateAction = "contact" | "description" | "name" | "owner";

export function EditableDetails({
  id,
  name,
  owner,
  contacts,
  description,
  readonlyContacts = false,
  readonlyOwner = false,
}: Props) {
  const [updateAction, setUpdateAction] = useState<UpdateAction | null>(null);

  const [updateApplication, { loading }] = useUpdateApplicationMutation();
  const { runMutation } = useMutationHandler();

  const handleUpdate = async (
    loaderName: UpdateAction,
    input: UpdateApplicationV2
  ) => {
    setUpdateAction(loaderName);

    await runMutation({
      identifier: `Application ${loaderName}`,
      mutation: () =>
        updateApplication({
          variables: {
            nid: id,
            input,
          },
        }),
      successAction: "updated",
    });

    setUpdateAction(null);
  };

  const activeLoader = loading ? updateAction : null;

  return (
    <div>
      <Text isTitle weight="medium">
        Application Details
      </Text>
      <FlexContainer alignItems="center">
        <DetailsTitle>Application Name:</DetailsTitle>
        <InlineUpdate
          width="65%"
          value={name}
          isLoading={activeLoader === "name"}
          onSubmit={(formData) =>
            handleUpdate("name", {
              name: formData.value,
              displayName: formData.value,
            })
          }
        />
      </FlexContainer>
      <Spacer height=".5rem" />
      <FlexContainer alignItems="center">
        <DetailsTitle>Application Owner:</DetailsTitle>
        <InlineContactsUpdate
          person={owner}
          isLoading={activeLoader === "owner"}
          readonly={readonlyOwner}
          canDelete={false}
          onSubmit={(formData) =>
            handleUpdate("owner", {
              ownerId: formData.personId,
            })
          }
        />
      </FlexContainer>
      <Spacer height=".5rem" />
      <FlexContainer alignItems="center">
        <DetailsTitle>Contact Person:</DetailsTitle>
        <InlineContactPicker
          initValues={contacts ?? []}
          property="contactPersonIds"
          required={false}
          readOnly={readonlyContacts}
          onSubmit={(formData) =>
            handleUpdate("contact", {
              contactIds: formData.contactPersonIds ?? [],
            })
          }
        />
      </FlexContainer>
      <Spacer height=".5rem" />
      <FlexContainer alignItems="center">
        <DetailsTitle>Description:</DetailsTitle>
        <InlineUpdate
          width="65%"
          value={description}
          isLoading={activeLoader === "description"}
          onSubmit={(formData) =>
            handleUpdate("description", {
              description: formData.value,
            })
          }
        />
      </FlexContainer>
    </div>
  );
}
